import { useTranslation } from 'react-i18next';

import { ModelDocumentation } from '@components/model/ModelDocumentation';
import ModelAttributeCard from '@components/model/details/ModelAttributeCard';
import FunctionalityOperationsList from '@components/model/functionality/operations/FunctionalityOperationsList';
import FunctionalityRecordsList from '@components/model/functionality/records/FunctionalityRecordsList';
import { Typography } from '@destination/components';

import useFunctionality from '@api/use-functionality';
import { IFunctionality } from '@models/IFunctionality';
import { IModel } from '@models/IModelBasic';
import { slugify } from '@utils/text-utils';
import { FunctionalitySettingsList } from './settings/FunctionalitySettingsList';
import { FunctionalityTelemetryList } from './telemetry/FunctionalityTelemetryList';
import { FunctionalityAttributesList } from './attributes/FunctionalityAttributesList';

export interface IFunctionalityDetailBody {
  model: IModel;
  defaultOpen?: boolean;
}

const ignoredModelAttributes = ['type', 'module', 'key', 'version', 'doc'];

export default function FunctionalityDetailBody({
  model,
  defaultOpen
}: IFunctionalityDetailBody) {
  const { t } = useTranslation(undefined, { keyPrefix: 'browse.model' });

  const { functionality, isLoading } = useFunctionality(model);

  const modelAttributes = functionality
    ? Object.keys(functionality)
        .filter(a => !ignoredModelAttributes.includes(a))
        .sort()
    : [];

  if (isLoading)
    return <Typography className="px-3">{t('loading')}</Typography>;

  return (
    <>
      {functionality?.doc && (
        <div className="flex justify-start ml-5 w-full">
          <ModelDocumentation doc={functionality.doc} />
        </div>
      )}
      {functionality &&
        modelAttributes.map(modelAttribute => (
          <ModelAttributeCard
            key={model.key + '-attribute-' + slugify(modelAttribute)}
            data-testid="model-attribute-card"
            title={modelAttribute}
            defaultOpen={defaultOpen}
          >
            {renderAttributeCard(functionality, modelAttribute)}
          </ModelAttributeCard>
        ))}
    </>
  );
}

const renderAttributeCard = (
  functionality: IFunctionality,
  attribute: string
) => {
  if (!functionality) return null;

  switch (attribute) {
    case 'records':
      if (!functionality.records) return <>No records provided!</>;
      return (
        <FunctionalityRecordsList
          data-testid="functionality-records-list"
          records={functionality.records}
        />
      );
    case 'operations':
      return (
        <FunctionalityOperationsList
          data-testid="functionality-operations-list"
          operations={functionality.operations}
          records={functionality.records ?? {}}
        />
      );
    case 'settings':
      if (!functionality.settings || functionality.settings.length == 0)
        return null;
      return (
        <FunctionalitySettingsList
          settings={functionality.settings}
          operations={functionality.operations}
          records={functionality.records ?? {}}
          data-testid="functionality-settings-list"
        />
      );
    case 'telemetry':
      if (!functionality.telemetry || functionality.telemetry.length == 0)
        return null;
      return (
        <FunctionalityTelemetryList
          telemetries={functionality.telemetry}
          operations={functionality.operations}
          records={functionality.records ?? {}}
          data-testid="functionality-telemetry-list"
        />
      );
    case 'attributes':
      if (
        !functionality.attributes ||
        Object.keys(functionality.attributes).length == 0
      ) {
        return <>No attributes provided</>;
      }
      if (!functionality.records) return <>No records provided!</>;

      return (
        <FunctionalityAttributesList
          attributes={functionality.attributes}
          records={functionality.records}
        />
      );
    default:
      return <>Not implemented yet!</>;
  }
};
